import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';

interface Certificate {
  id: string;
  code: string;
  recipient_name: string;
  class_name: string;
  issued_at: string;
  is_revoked: boolean;
  class?: {
    id: string;
    title: string;
  };
  provider?: {
    id: string;
    company_name: string;
  };
}

/**
 * 인증서 보관함(디지털 월렛) — 메인페이지 탭에 인라인으로 표시.
 * 페이지 버전은 MyCertificatesPage 참고.
 */
export default function CertificateWallet({ dark = false }: { dark?: boolean } = {}) {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    const fetchCertificates = async () => {
      try {
        const response = await fetch('/api/v1/certificates', { credentials: 'include' });
        if (!response.ok) throw new Error('인증서 목록을 불러오는데 실패했습니다.');
        const data = await response.json();
        setCertificates(data.certificates || []);
      } catch (error: any) {
        console.error('Failed to fetch certificates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCertificates();
  }, [user]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });

  if (!user) {
    return (
      <div className="bg-white rounded-2xl shadow-soft border border-gray-200 p-12 text-center">
        <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 text-lg mb-4">로그인 후 인증서 보관함을 확인할 수 있습니다.</p>
        <button
          onClick={() => navigate('/login')}
          className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
        >
          로그인
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 text-center">
        <h2 className={`text-3xl sm:text-4xl font-bold mb-1 ${dark ? 'text-white' : 'text-gray-900'}`}>
          인증서 보관함
        </h2>
        <p className={`text-sm ${dark ? 'text-white/70' : 'text-gray-600'}`}>
          발급받은 인증서를 확인하고 관리할 수 있습니다.
        </p>
      </div>

      {certificates.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-soft border border-gray-200 p-12 text-center">
          <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg mb-2">발급받은 인증서가 없습니다.</p>
          <p className="text-gray-500 text-sm">수업을 완료하고 승인받으면 인증서가 발급됩니다.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certificates.map((cert) => (
            <div
              key={cert.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden"
            >
              <div className="bg-gradient-to-r from-primary to-primary/80 p-4">
                <div className="flex items-center gap-2 text-white">
                  <Award className="w-5 h-5" />
                  <span className="text-sm font-semibold">인증서</span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2 text-lg line-clamp-2">{cert.class_name}</h3>
                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex justify-between">
                    <span>발급 업체:</span>
                    <span className="font-medium text-gray-900">{cert.provider?.company_name || '알 수 없음'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>발급일:</span>
                    <span className="font-medium text-gray-900">{formatDate(cert.issued_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>인증번호:</span>
                    <span className="font-mono text-xs text-gray-700">{cert.code}</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/certificates/${cert.id}/view`)}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  인증서 보기
                </button>
                {cert.is_revoked && (
                  <div className="mt-2 text-center">
                    <span className="inline-block px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded">
                      취소됨
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
